import React, { useState } from "react";
import { View, StyleSheet, Button, Text, Alert, Linking } from "react-native";
import * as ImagePicker from "expo-image-picker";
import * as DocumentPicker from "expo-document-picker";
import * as FileSystem from "expo-file-system/legacy";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import {
  Menu,
  MenuOptions,
  MenuOption,
  MenuTrigger,
} from "react-native-popup-menu";

export type FileProps = {
  uri: string;
  type: string;
  base64EncodedFile: string;
  name?: string;
};

interface FileInputProps {
  onFileSelect: (files: FileProps | FileProps[]) => void;
  hidePhotoOption?: boolean;
  hideDocumentOption?: boolean;
  hideImageOption?: boolean;
  allowMultiple?: boolean;
  maxFiles?: number;
}

interface MenuOptionType {
  text: string;
  onSelect: () => void;
  icon: keyof typeof MaterialCommunityIcons.glyphMap;
  hidden?: boolean;
}

const FileInput: React.FC<FileInputProps> = ({
  onFileSelect,
  hideDocumentOption,
  hideImageOption,
  hidePhotoOption,
  allowMultiple = false,
  maxFiles = 5
}) => {
  const [visible, setVisible] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<FileProps[]>([]);

  const handleSelect = (action: () => Promise<void>) => {
    setVisible(false);
    action().then();
  };

  const handleFilesUpdate = (newFiles: FileProps[]) => {
    let updatedFiles: FileProps[];
    if (allowMultiple) {
      updatedFiles = [...uploadedFiles, ...newFiles].slice(0, maxFiles);
    } else {
      updatedFiles = [newFiles[0]];
    }
    setUploadedFiles(updatedFiles);
    onFileSelect(allowMultiple ? updatedFiles : updatedFiles[0]);
  };

  const pickImageFromGallery = async () => {
    const permissionResult =
      await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!permissionResult.granted) {
      Alert.alert(
        "Permission Required",
        "This app needs access to your photos to continue",
        [
          {
            text: "Cancel",
            style: "cancel"
          },
          {
            text: "Settings",
            onPress: () => Linking.openSettings()
          }
        ]
      );
      return;
    }
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.All,
      allowsEditing: true,
      aspect: [4, 3],
      quality: 1,
      base64: true,
      allowsMultipleSelection: allowMultiple,
      selectionLimit: maxFiles
    });

    if (!result.canceled && result.assets) {
      const newFiles = result.assets.map(asset => ({
        uri: asset.uri,
        type: asset.mimeType as string,
        base64EncodedFile: asset.base64 || '',
        name: asset.fileName || 'Image'
      }));
      handleFilesUpdate(newFiles);
    }
  };

  const takePhoto = async () => {
    const permissionResult = await ImagePicker.requestCameraPermissionsAsync();
    if (!permissionResult.granted) {
      Alert.alert(
        "Permission Required",
        "This app needs access to your camera to continue",
        [
          {
            text: "Cancel",
            style: "cancel"
          },
          {
            text: "Settings",
            onPress: () => Linking.openSettings()
          }
        ]
      );
      return;
    }
    const result = await ImagePicker.launchCameraAsync({
      allowsEditing: true,
      aspect: [4, 3],
      quality: 1,
      base64: true,
    });
    if (!result.canceled && result.assets) {
      const newFile = {
        uri: result.assets[0].uri,
        type: result.assets[0].mimeType as string,
        base64EncodedFile: result.assets[0].base64 || '',
        name: result.assets[0].fileName || 'Photo'
      };
      handleFilesUpdate([newFile]);
    }
  };

  const pickDocument = async () => {
    const result = await DocumentPicker.getDocumentAsync({
      type: "application/pdf",
      multiple: allowMultiple
    });
    if (!result.canceled && result.assets) {
      const newFiles = await Promise.all(result.assets.map(async asset => {
        const base64 = await FileSystem.readAsStringAsync(asset.uri, { encoding: 'base64' });
        return {
          uri: asset.uri,
          type: asset.mimeType as string,
          base64EncodedFile: base64,
          name: asset.name || 'Document'
        };
      }));
      handleFilesUpdate(newFiles);
    }
  };

  const removeFile = (index: number) => {
    const updatedFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(updatedFiles);
    onFileSelect(allowMultiple ? updatedFiles : updatedFiles[0]);
  };

  const menuOptions: MenuOptionType[] = [
    {
      text: `Select document${allowMultiple ? '(s)' : ''}`,
      onSelect: () => handleSelect(pickDocument),
      icon: "file-pdf-box",
      hidden: hideDocumentOption
    },
    {
      text: `Select image${allowMultiple ? '(s)' : ''}`,
      onSelect: () => handleSelect(pickImageFromGallery),
      icon: "file-image-outline",
      hidden: hideImageOption
    },
    {
      text: "Take a photo",
      onSelect: () => handleSelect(takePhoto),
      icon: "camera",
      hidden: hidePhotoOption
    },
  ];

  return (
    <View>
      {uploadedFiles.length > 0 && (
        <View style={styles.fileList}>
          {uploadedFiles.map((file, index) => (
            <View key={index} style={styles.fileItem}>
              <Text style={styles.fileName}>File {index + 1}: {file.name}</Text>
              <Text style={styles.removeButton} onPress={() => removeFile(index)}>
                <MaterialCommunityIcons name="close" size={20} />
              </Text>
            </View>
          ))}
        </View>
      )}

      {(!allowMultiple || uploadedFiles.length < maxFiles) && (
        <Menu onBackdropPress={() => setVisible(false)}>
          <MenuTrigger
            text={uploadedFiles.length > 0 ? (allowMultiple ? "Add more files" : "Change file") : "Select file"}
            customStyles={{
              triggerText: {
                color: "#fff000",
                textTransform: "uppercase",
                fontWeight: "bold",
                fontSize: 14,
              },
              triggerWrapper: {
                backgroundColor: "black",
                borderRadius: 8,
                paddingHorizontal: 16,
                paddingVertical: 10,
                alignItems: "center",
                justifyContent: "center",
              },
            }}
          />
          <MenuOptions
            customStyles={{
              optionsContainer: {
                borderRadius: 10,
              },
              optionWrapper: {
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
              },
            }}
          >
            {menuOptions.filter((x) => !x.hidden).map((option, index) => (
              <View key={index}>
                <MenuOption onSelect={option.onSelect} style={styles.menuOption}>
                  <Text style={styles.textStyle}>{option.text}</Text>
                  <Text style={styles.iconStyle}>
                    <MaterialCommunityIcons name={option.icon} size={20} />
                  </Text>
                </MenuOption>
              </View>
            ))}
          </MenuOptions>
        </Menu>
      )}
    </View>
  );
};

export default FileInput;

const styles = StyleSheet.create({
  menuContainer: {
    flex: 1,
    backgroundColor: "#fff",
    justifyContent: "center",
    alignItems: "center",
  },
  menuOption: {
    padding: 8,
  },
  iconStyle: {
    marginRight: 10,
  },
  textStyle: {
    fontSize: 14,
  },
  fileList: {
    marginBottom: 10,
  },
  fileItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 5,
  },
  fileName: {
    fontSize: 14,
    color: "black",
    flex: 1,
  },
  removeButton: {
    padding: 5,
    color: "red",
  }
});
